import { useState, useRef, useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";
import { message } from "antd";
import { v4 as uuidv4 } from "uuid";
import { ApolloError } from "@apollo/client";
import { nanoid } from "nanoid";
import dayjs from "dayjs";

import { clearSelectedAttachments } from "state/reactive-vars/selected-attachment-var";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { useProductCategories } from "hooks/useProductCategories";

import { useAddDeviceDetectionJobMutation } from "graphql/mutations/add-device-detection-job.generated";
import { useGetDeviceDetectionByIdSubscription } from "graphql/subscriptions/device-detection-job.generated";
import { useGetUserPropertiesFilteredQuery } from "graphql/queries/get-user-properties-filtered.generated";
import {
	Properties,
	Maybe,
	Device_Detection_Jobs,
	ProductWithInstalledProductsInput,
	NestedInstalledProductInput,
	AttachmentsInputType
} from "graphql/generated/graphql";
import { useCreateProductsWithInstallationsMutation } from "graphql/mutations/create-products-with-installations.generated";
import { useUpdateDeviceDetectionJobByIdMutation } from "graphql/mutations/update-device-detection-job-by-id.generated";
import { useUpdateHandoverByIdMutation } from "graphql/mutations/update-handover-by-id.generated";

import { MediaType, DeviceDetectionJobStatus, InstalledProductWarrantyStatus } from "constants/field-values";
import {
	getFileExtension,
	getFileNameFromUri,
	getFileTypeFromMimeType,
	createFirebaseFileName,
	uploadToStorage,
	convertHeicFileToJpeg
} from "utils/files-management";
import { pruneEmptyValues } from "utils/helpers";

// Product category type - used throughout the app
export interface ProductCategory {
	name: string; // e.g., "ACCESS_CONTROL" - used for DB
	label: string; // e.g., "Access Control" - used for UI display
}

// Extended attachment type that can hold both remote URLs and local files
export interface AttachmentWithFile extends AttachmentsInputType {
	originFileObj?: File; // Local file object, present when file hasn't been uploaded yet
	uid?: string; // Unique identifier for tracking in UI
	localUrl?: string;
}

export interface DetectedDeviceFile {
	file_uri: string;
	file_type: string; // "photo" | "document";
	mime_type: string;
}

export interface MatchedCatalogProduct {
	id: string;
	make: string;
	name: string;
	type: ProductCategory;
	model: string;
}

export interface DeviceInstallation extends Omit<NestedInstalledProductInput, "attachments"> {
	draft_installed_product_id?: string;
	installation_visible?: boolean;
	installed_device_index?: number;
	serial_number_confidence?: "high" | "medium" | "low" | "not_visible";
	attachments?: AttachmentWithFile[];
	installedProductPhotoFile?: any; // RcFile from photo picker, present when photo needs to be uploaded
}

export interface DetectedDevice extends Omit<
	ProductWithInstalledProductsInput,
	"installed_products" | "attachments" | "type"
> {
	draft_product_id: string;
	type: ProductCategory;
	match_confidence?: "high" | "medium" | "low" | "no_match";
	installed_products: DeviceInstallation[];
	attachments?: AttachmentWithFile[];
	productPhotoFile?: any; // RcFile from photo picker, present when photo needs to be uploaded
}

export interface DeviceDetectionCategory {
	product_type: ProductCategory;
	products: DetectedDevice[];
	totalProducts: number;
	totalInstalledProducts: number;
}

export interface DeviceDetectionValues {
	jobId: string | null;
	job: Maybe<Device_Detection_Jobs>;
	productsGroupedByProductType: DeviceDetectionCategory[];
	hasChanges: boolean;
}

/**
 * Upload a product photo to Firebase Storage
 * @param photoFile - The RcFile object from the photo picker
 * @param productName - Name of the product (used in filename)
 * @returns Download URL of the uploaded photo
 */
export const uploadProductPhoto = async (photoFile: any, productName: string = "product"): Promise<string> => {
	const uniqueID = uuidv4();
	const timestamp = Math.floor(Date.now() / 1000);
	const fileExtension = getFileExtension(photoFile.name);
	const fileName = `product-photo-${productName}-${uniqueID}-${timestamp}.${fileExtension}`;

	let finalFile = photoFile;
	if (/\.(heic|heif)$/i.test(finalFile.name) || finalFile.type === "image/heic" || finalFile.type === "image/heif") {
		try {
			finalFile = await convertHeicFileToJpeg(finalFile);
		} catch (error) {
			console.error("HEIC conversion failed:", error);
		}
	}

	const bucketPath = "product-photos";
	const { publicUrl } = await uploadToStorage(finalFile, () => {}, `${bucketPath}/${fileName}`)

	return publicUrl;
};

/**
 * Upload attachment files to Firebase and return updated attachment object
 * @param attachment - Attachment object that may contain originFileObj
 * @param baseBucketPath - Base bucket path (e.g., 'device-detection-files')
 * @returns Updated attachment with download URL
 */
export const uploadAttachmentFile = async (
	attachment: any,
	baseBucketPath: string = "device-detection-files"
): Promise<any> => {
	// If no local file, return as-is
	if (!attachment.originFileObj) {
		return attachment;
	}

	let fileOrg = attachment.originFileObj;
	if (/\.(heic|heif)$/i.test(fileOrg.name) || fileOrg.type === "image/heic" || fileOrg.type === "image/heif") {
		try {
			fileOrg = await convertHeicFileToJpeg(fileOrg);
		} catch (error) {
			console.error("HEIC conversion failed:", error);
		}
	}
	const name = attachment.name;
	const fileType = attachment.type || "document";
	const mimeType = fileOrg.type;
	const mediaType = mimeType.split("/")[0];

	let bucketSubPath = "";
	let filePrefix = "";
	let linkField = "document_link";

	// Determine bucket path and prefix based on file type
	switch (mediaType) {
		case "image":
			bucketSubPath = "photos";
			filePrefix = "photo";
			linkField = "photo_link";
			break;
		case "video":
			bucketSubPath = "videos";
			filePrefix = "video";
			linkField = "video_link";
			break;
		case "audio":
			bucketSubPath = "audios";
			filePrefix = "audio";
			linkField = "audio_link";
			break;
		default:
			bucketSubPath = "documents";
			filePrefix = "document";
			linkField = "document_link";
			break;
	}	
	const bucketPath = `${baseBucketPath}/${bucketSubPath}`;
	const fileName = createFirebaseFileName(fileOrg, filePrefix, name)
	const { publicUrl } = await uploadToStorage(fileOrg, () => {}, `${bucketPath}/${fileName}`);

	// Return updated attachment with download URL and without originFileObj
	const { originFileObj, uid, localUrl, file, ...rest } = attachment;
	if (localUrl) {
		URL.revokeObjectURL(localUrl);
	}

	return {
		...rest,
		name,
		type: fileType,
		mime_type: mimeType,
		site_link: null,
		audio_link: null,
		photo_link: null,
		video_link: null,
		document_link: null,
		description: null,
		doctrine_doc_id: null,
		[linkField]: publicUrl // Override the specific link field
	};
};

/**
 * Upload an installed product photo to Firebase Storage
 * @param photoFile - The RcFile object from the photo picker
 * @param serial - Serial number of the installed product (used in filename)
 * @returns Download URL of the uploaded photo
 */
export const uploadInstalledProductPhoto = async (
	photoFile: any,
	serial: string = "installed-product"
): Promise<string> => {
	const uniqueID = uuidv4();
	const timestamp = Math.floor(Date.now() / 1000);
	const fileExtension = getFileExtension(photoFile.name);
	const fileName = `installed-product-photo-${serial}-${uniqueID}-${timestamp}.${fileExtension}`;

	let finalFile = photoFile;
	if (/\.(heic|heif)$/i.test(finalFile.name) || finalFile.type === "image/heic" || finalFile.type === "image/heif") {
		try {
			finalFile = await convertHeicFileToJpeg(finalFile);
		} catch (error) {
			console.error("HEIC conversion failed:", error);
		}
	}

	const bucketPath = "installed-product-photos";

	const { publicUrl } = await uploadToStorage(finalFile, () => {}, `${bucketPath}/${fileName}`)

	return publicUrl;
};

/**
 * Process all attachments in a product, uploading local files and returning clean attachment list
 * @param attachments - Array of attachments that may contain local files
 * @returns Array of attachments with uploaded files
 */
export const processProductAttachments = async (attachments?: any[], bucket?: string): Promise<any[]> => {
	if (!attachments || attachments.length === 0) {
		return [];
	}

	const uploadPromises = attachments.map(attachment => uploadAttachmentFile(attachment, bucket));

	return await Promise.all(uploadPromises);
};

export const pickFirstPhotoLink = (files?: any[]) => {
	const imgAttachment = (files ?? []).find(f => {
		if (f?.mime_type?.startsWith("image/") && f?.file_uri) {
			return true;
		}

		if (f?.photo_link) {
			return true;
		}

		return false;
	});

	if (!imgAttachment) return null;

	const link = imgAttachment?.photo_link ?? imgAttachment?.file_uri;

	return link;
};

export const filesToAttachments = (files?: any[]) => {
	if (!files?.length) return undefined;

	return files
		.filter(f => f?.file_uri)
		.map(f => {
			const fileUri = String(f.file_uri);
			const fileType = String(f.file_type || "").toLowerCase();
			const fileMimeType = String(f.mime_type);

			const base = {
				name: getFileNameFromUri(fileUri),
				description: null,
				doctrine_doc_id: null,
				type: fileType,
				site_link: null,
				photo_link: null,
				document_link: null,
				video_link: null,
				audio_link: null,
				mime_type: fileMimeType
			};

			switch (fileType) {
				case "photo":
					return { ...base, photo_link: fileUri };
				case "document":
					return { ...base, document_link: fileUri };
				case "video":
					return { ...base, video_link: fileUri };
				case "audio":
					return { ...base, audio_link: fileUri };
				default:
					return { ...base, document_link: fileUri };
			}
		});
};

export const formatInstalledProduct = (ip: any) => {
	return {
		property_id: ip.property_id, // required
		product_serial: ip.product_serial ?? ip.serial_number,
		location: ip.location ?? ip.location_description,
		// Structured location (only the deepest level is needed; the DB trigger derives ancestors).
		building_id: ip.building_id ?? null,
		floor_id: ip.floor_id ?? null,
		room_id: ip.room_id ?? null,
		product_notes: ip.product_notes ?? ip.additional_notes,
		installation_date: ip.installation_date ? dayjs(ip.installation_date).format("YYYY-MM-DDTHH:mm:ssZ") : null,
		warranty_expiration_date: ip.warranty_expiration_date
			? dayjs(ip.warranty_expiration_date).format("YYYY-MM-DDTHH:mm:ssZ")
			: null,
		warranty_status: ip.warranty_status,
		photo_link: ip.photo_link,
		attachments: ip.attachments,
		handover_id: ip.handover_id,
		device_detection_job_id: ip.device_detection_job_id
	};
};

// ─── Types ────────────────────────────────────────────────────────────────────

// ── Product state helpers (pure functions, operate on DeviceDetectionCategory[]) ──

/**
 * Find a product by draft_product_id across all groups.
 * Returns indexes + product reference, or null if not found.
 */
const findProductByDraftId = (
	groups: DeviceDetectionCategory[],
	draft_product_id: string
): { groupIdx: number; productIdx: number; product: DetectedDevice } | null => {
	for (let groupIdx = 0; groupIdx < groups.length; groupIdx++) {
		const productIdx = groups[groupIdx].products.findIndex(p => p.draft_product_id === draft_product_id);
		if (productIdx !== -1) {
			return { groupIdx, productIdx, product: groups[groupIdx].products[productIdx] };
		}
	}
	return null;
};

/**
 * Find an installed product by draft_installed_product_id across all groups.
 */
const findInstalledProductByDraftId = (
	groups: DeviceDetectionCategory[],
	draft_installed_product_id: string
): { groupIdx: number; productIdx: number; installedIdx: number } | null => {
	for (let groupIdx = 0; groupIdx < groups.length; groupIdx++) {
		for (let productIdx = 0; productIdx < groups[groupIdx].products.length; productIdx++) {
			const installedIdx = groups[groupIdx].products[productIdx].installed_products.findIndex(
				ip => ip.draft_installed_product_id === draft_installed_product_id
			);
			if (installedIdx !== -1) return { groupIdx, productIdx, installedIdx };
		}
	}
	return null;
};

/**
 * Recompute totalProducts and totalInstalledProducts for a single group
 * by counting the actual arrays — avoids bugs from maintaining counters manually.
 */
const recalculateGroupTotals = (group: DeviceDetectionCategory): DeviceDetectionCategory => ({
	...group,
	totalProducts: group.products.length,
	totalInstalledProducts: group.products.reduce((sum, p) => sum + (p.installed_products?.length ?? 0), 0)
});

/**
 * Remove groups that have no products left.
 * Called after any operation that can leave a group empty.
 */
const removeEmptyGroups = (groups: DeviceDetectionCategory[]): DeviceDetectionCategory[] =>
	groups.filter(g => g.products.length > 0);

export interface FileViewerState {
	type: MediaType | null;
	src: string;
}

// All values and handlers the hook exposes to both consumer components
export interface DeviceDetectionCoreResult {
	// State
	propertyOptions: Array<Properties>;
	selectedProperty: Maybe<Properties>;
	setSelectedProperty: (p: Maybe<Properties>) => void;
	attachmentsFiles: Array<any>;
	addAttachmentModalOpen: boolean;
	setAddAttachmentModalOpen: (open: boolean) => void;
	addAttachmentModalEditMode: boolean;
	jobId: string | null;
	processingStarted: boolean;
	setProcessingStarted: (v: boolean) => void;
	setJobId: (id: string | null) => void;
	setAttachmentsFiles: React.Dispatch<React.SetStateAction<Array<any>>>;
	vendorDeviceNotes: string;
	setVendorDeviceNotes: (v: string) => void;
	productModalOpen: boolean;
	selectedProduct: any;
	installedProductModalOpen: boolean;
	selectedInstallation: any;
	addInstallModalOpen: boolean;
	selectedProductForNewInstall: DetectedDevice | null;
	addProductModalOpen: boolean;
	fileViewer: FileViewerState;
	setFileViewer: (v: FileViewerState) => void;
	isLoading: boolean;
	propertiesLoading: boolean;
	uploadingDetectionFiles: boolean;
	allInstalledProductsSkipped: boolean;

	// Store
	job: any;
	productsGroupedByProductType: any[];

	// Subscription
	subscriptionData: any;
	processingData: any;

	// Handlers
	handleOnBack: () => void;
	handleOpenProductModal: (product: any) => void;
	handleCloseProductModal: () => void;
	handleConfirmProductDeletion: (product: any) => void;
	handleOpenInstalledProductModal: (installation: any) => void;
	handleCloseInstalledProductModal: () => void;
	handleConfirmInstalledProductDeletion: (ip: any) => void;
	handleOpenAddInstallModal: (product: DetectedDevice) => void;
	handleCloseAddInstallModal: () => void;
	addInstalledProduct: (draft_product_id: string, install: Partial<DeviceInstallation>) => Promise<void>;
	handleOpenAddProductModal: () => void;
	handleCloseAddProductModal: () => void;
	addProduct: (product: Partial<DetectedDevice>) => Promise<void>;
	handleOnCloseAttachmentModal: () => void;
	handleOnRemoveAttachment: (file: any) => void;
	updateAttachments: (file: any) => void;
	handleOnClickAttachment: (file: any) => void;
	handleOnSubmit: () => Promise<void>;
	publishDetection: () => Promise<void>;
	updateProduct: (draft_product_id: string, updates: Partial<DetectedDevice>) => Promise<void>;
	updateInstalledProduct: (draft_installed_product_id: string, updates: Partial<DeviceInstallation>) => Promise<void>;

	// Helpers
	getLabelFromName: (name: string) => string;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * useDeviceDetectionCore
 *
 * Contains ALL business logic shared between:
 *   - DeviceDetectionJob   (admin standalone, /admin/device-detection/...)
 *   - HandoverDeviceDetection (handover context, /handover-device-detection/...)
 *
 * The hook is navigation-context-agnostic: callers pass `onBack` and
 * `onJobReady` so each consumer can decide where to navigate.
 *
 * @param onBack       - Called when the user cancels or publishes; navigates to
 *                       the appropriate parent screen.
 * @param onJobReady   - Called once the subscription resolves the job to
 *                       DRAFT/PUBLISHED/FAILED; receives the job id so the
 *                       caller can navigate to the detail route.
 * @param handoverId  - Present only in handover context; used to link
 *                              the new job to the handover and to save notes.
 * @param propertyId  - Present only in handover context; auto-selects
 *                              the property.
 */
export const useDeviceDetectionCore = ({
	onBack,
	onJobReady,
	handoverId,
	propertyId,
	detectionJobId,
	initialProperty
}: {
	onBack: () => void;
	onJobReady: (jobId: string) => void;
	handoverId?: string;
	propertyId?: string;
	detectionJobId?: string;
	initialProperty?: Properties | null;
}): DeviceDetectionCoreResult => {
	const { loggedUserValue } = useLoggedUser();
	const params = useParams<{ jobId: string }>();

	// ── State ────────────────────────────────────────────────────────────────

	const [propertyOptions, setPropertyOptions] = useState<Array<Properties>>([]);
	const [selectedProperty, setSelectedProperty] = useState<Maybe<Properties>>(null);
	const [attachmentsFiles, setAttachmentsFiles] = useState<Array<any>>([]);
	const [addAttachmentModalOpen, setAddAttachmentModalOpen] = useState(false);
	const [addAttachmentModalEditMode, setAddAttachmentModalEditMode] = useState(false);
	const [uploading, setUploading] = useState(false);

	const initialJobId = detectionJobId || params.jobId || null;

	const [jobId, setJobId] = useState<string | null>(initialJobId);
	const [processingStarted, setProcessingStarted] = useState(!!params.jobId || !!jobId);
	const [vendorDeviceNotes, setVendorDeviceNotes] = useState("");

	// Update jobId when detectionJobId changes (HandoverDetails case)
	useEffect(() => {
		if (detectionJobId && detectionJobId !== jobId) {
			setJobId(detectionJobId);
			setProcessingStarted(true);
		}
	}, [detectionJobId, jobId]);

	// When the property comes from handover context (not necessarily in user_properties)
	useEffect(() => {
		if (!initialProperty || !propertyId) return;
		if (initialProperty.id !== propertyId) return;
		// Functional update avoids adding selectedProperty to deps while preventing redundant sets
		setSelectedProperty(prev => (prev?.id === propertyId ? prev : (initialProperty as Properties)));
	}, [initialProperty, propertyId]);

	const [productModalOpen, setProductModalOpen] = useState(false);
	const [selectedProduct, setSelectedProduct] = useState<any>(null);
	const [installedProductModalOpen, setInstalledProductModalOpen] = useState(false);
	const [selectedInstallation, setSelectedInstallation] = useState<any>(null);
	const [addInstallModalOpen, setAddInstallModalOpen] = useState(false);
	const [selectedProductForNewInstall, setSelectedProductForNewInstall] = useState<DetectedDevice | null>(null);
	const [addProductModalOpen, setAddProductModalOpen] = useState(false);
	const [fileViewer, setFileViewer] = useState<FileViewerState>({ type: null, src: "" });
	const [isPublishing, setIsPublishing] = useState(false);
	// True when batch_create completes but every installed product was skipped
	// (e.g. all serial numbers already exist in DB). Job stays in DRAFT.
	const [allInstalledProductsSkipped, setAllInstalledProductsSkipped] = useState(false);

	/* 
	productsGroupedByProductType is initialized from the subscription on first load (when isInitialized is false) and updated optimistically on every edit.
	After initialization the subscription is NOT used to overwrite it, the local state is the source of truth while the job is open,
	and every mutation persists it to products_grouped_by_type in the DB
	*/
	const [productsGroupedByProductType, setProductsGroupedByProductType] = useState<DeviceDetectionCategory[]>([]);
	const [job, setJob] = useState<any>(null);

	// Reset state whenever the active jobId changes (navigation to a different job)
	const isInitialized = useRef(false);
	// Prevents double-submission (double-click or rapid re-entry before state update)
	const isSubmittingRef = useRef(false);
	useEffect(() => {
		isInitialized.current = false;
		setProductsGroupedByProductType([]);
		setJob(null);
		setAllInstalledProductsSkipped(false);
	}, [jobId]);

	const { getLabelFromName, getCategoryFromName } = useProductCategories();

	const handleOpenProductModal = useCallback((product: any) => {
		setSelectedProduct(product);
		setProductModalOpen(true);
	}, []);

	const handleCloseProductModal = useCallback(() => {
		setProductModalOpen(false);
		setSelectedProduct(null);
	}, []);

	const handleOpenInstalledProductModal = useCallback((installation: any) => {
		setSelectedInstallation(installation);
		setInstalledProductModalOpen(true);
	}, []);

	const handleCloseInstalledProductModal = useCallback(() => {
		setInstalledProductModalOpen(false);
		setSelectedInstallation(null);
	}, []);

	const handleOpenAddInstallModal = useCallback((product: DetectedDevice) => {
		setSelectedProductForNewInstall(product);
		setAddInstallModalOpen(true);
	}, []);

	const handleCloseAddInstallModal = useCallback(() => {
		setAddInstallModalOpen(false);
		setSelectedProductForNewInstall(null);
	}, []);

	const handleOpenAddProductModal = useCallback(() => {
		setAddProductModalOpen(true);
	}, []);

	const handleCloseAddProductModal = useCallback(() => {
		setAddProductModalOpen(false);
	}, []);

	const handleOnCompletePropertiesFiltered = useCallback(
		(data: any) => {
			const userProperties = data.user_properties.map((up: any) => up.property);
			const options = userProperties.map((up: any) => ({
				value: JSON.stringify(up),
				label: up.name
			}));
			setPropertyOptions(options);

			// Auto-select property when coming from handover context
			if (propertyId && userProperties.length > 0) {
				const match = userProperties.find((p: any) => p.id === propertyId);
				if (match) setSelectedProperty(match);
			}
		},
		[propertyId]
	);

	const handleOnErrorPropertiesFiltered = useCallback((error: ApolloError) => {
		console.error("handleOnErrorPropertiesFiltered Error: ", error);
	}, []);

	const { loading: propertiesLoading } = useGetUserPropertiesFilteredQuery({
		skip: !loggedUserValue?.id,
		variables: {
			where: {
				user_id: { _eq: loggedUserValue.id },
				property: { deleted_at: { _is_null: true } }
			}
		},
		errorPolicy: "none",
		fetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onCompleted: handleOnCompletePropertiesFiltered,
		onError: handleOnErrorPropertiesFiltered
	});

	// ── Create job mutation ───────────────────────────────────────────────────

	const handleOnCompleteCreateJob = useCallback((data: any) => {
		console.log("handleOnCompleteCreateJob data: ", data);
		const id = data?.insert_device_detection_jobs_one?.id;
		if (id) {
			setJobId(id);
			setProcessingStarted(true);
			message.success("Automagical device detection started successfully");
		}
		setUploading(false);
	}, []);

	const handleOnErrorCreateJob = useCallback((_error: ApolloError) => {
		setUploading(false);
		message.error("Failed to create device detection job");
	}, []);

	const [createJobMutation] = useAddDeviceDetectionJobMutation({
		errorPolicy: "none",
		fetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorCreateJob,
		onCompleted: handleOnCompleteCreateJob
	});

	// ── Update handover mutation (handover context only) ─────────────────────

	const [updateHandoverByIdMutation, { loading: loadingUpdateHandover }] = useUpdateHandoverByIdMutation({
		errorPolicy: "none",
		fetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onCompleted: data => console.log("Update Handover completed:", data),
		onError: error => console.error("Update Handover error:", error)
	});

	// ── Update job mutation ───────────────────────────────────────────────────

	const handleOnCompleteUpdateJob = useCallback((data: any) => {
		console.log("UpdateJob completed:", data);
	}, []);

	const [updateDeviceDetectionJobByIdMutation, { loading: loadingUpdateJob }] = useUpdateDeviceDetectionJobByIdMutation(
		{
			errorPolicy: "none",
			fetchPolicy: "network-only",
			notifyOnNetworkStatusChange: true,
			onError: error => {
				console.error("UpdateJob error:", error);
				message.error("Failed to update device detection job");
			},
			onCompleted: handleOnCompleteUpdateJob
		}
	);

	// ── Create products mutation ──────────────────────────────────────────────

	const handleOnCompleteCreateProducts = useCallback(
		async (data: any) => {
			console.log("CreateProducts completed:", data);

			const result = data?.batch_create_products_with_installed_products;
			const totalIPs = result?.total_installed_products ?? 0;
			const successfulIPs = result?.successful_installed_products ?? 0;

			// All installed products were skipped (e.g. every serial number already exists in DB).
			// Keep the job in DRAFT so the vendor can cancel and start over with new devices.
			// Do NOT publish and do NOT navigate away.
			if (totalIPs > 0 && successfulIPs === 0) {
				console.warn("batch_create: all installed products skipped", result?.results);
				setAllInstalledProductsSkipped(true);
				return;
			}

			if (jobId) {
				await updateDeviceDetectionJobByIdMutation({
					variables: {
						id: jobId,
						_set: { status: DeviceDetectionJobStatus.PUBLISHED }
					}
				});

				if (onBack) {
					onBack();
				}
			}
		},
		[jobId, updateDeviceDetectionJobByIdMutation, onBack]
	);

	const [createProductsWithInstallationsMutation, { loading: loadingCreateProducts }] =
		useCreateProductsWithInstallationsMutation({
			errorPolicy: "none",
			fetchPolicy: "network-only",
			notifyOnNetworkStatusChange: true,
			onError: error => {
				console.error("CreateProducts error:", error);
				message.error("Failed to save products");
			},
			onCompleted: handleOnCompleteCreateProducts,
			refetchQueries: ["getHandoverById"]
		});

	//  Group detected installations by product_type -> products -> detected_installed_products
	const groupProductsByProductType = useCallback(
		(job: any) => {
			if (!job?.detected_devices || !job?.detected_installed_products) {
				return { productsGroupedByType: [], categoriesList: [] };
			}

			try {
				const products =
					typeof job.detected_devices === "string" ? JSON.parse(job.detected_devices) : job.detected_devices;

				const installed_products =
					typeof job.detected_installed_products === "string"
						? JSON.parse(job.detected_installed_products)
						: job.detected_installed_products;

				if (!products || products.length === 0) {
					return { productsGroupedByType: [], categoriesList: [] };
				}

				// First, group products by product_type (using category name as key)
				const categoriesMap = new Map<string, any[]>();
				const categoriesList: string[] = [];

				// Resolves a raw AI category string (e.g. "gate_operator") to a ProductCategory.
				// Step 1: tries the DB-backed categoryMap (e.g. "ACCESS_CONTROL" → { name, label }).
				// Step 2: if no DB match, builds a ProductCategory from the raw AI string
				//         e.g. "gate_operator" → { name: "gate_operator", label: "Gate Operator" }
				const resolveCategory = (rawName: string): ProductCategory | null => {
					if (!rawName) return null;
					const matched = getCategoryFromName(rawName);
					if (matched) return matched;
					// No DB match — derive label from the raw string ("gate_operator" → "Gate Operator")
					const label = rawName
						.split("_")
						.map(word => word.charAt(0).toUpperCase() + word.slice(1))
						.join(" ");
					return { name: rawName, label };
				};

				products.forEach((device: any) => {
					// Use detected_category as the key (e.g., "ACCESS_CONTROL" or "gate_operator")
					const categoryName = device.detected_category || "";
					if (!categoriesMap.has(categoryName)) {
						categoriesMap.set(categoryName, []);
						categoriesList.push(categoryName);
					}
					categoriesMap.get(categoryName)!.push(device);
				});

				// Then, for each product_type, create the structure with products and their installations
				const categoriesWithDevices = Array.from(categoriesMap.entries()).map(([categoryName, categoryDevices]) => {
					// Get the ProductCategory object from the name
					const productTypeCategory = resolveCategory(categoryName);

					const productsWithInstalledProducts = categoryDevices.map((device: any) => {
						const {
							matched_catalog_product_id,
							match_confidence,
							detected_make,
							detected_model,
							visual_description,
							detected_category,
							visible_in_files
						} = device;

						const relatedInstallations: DeviceInstallation[] = [];

						installed_products.forEach((installation: any) => {
							const {
								serial_number,
								location_description,
								additional_notes,
								installation_visible,
								installed_device_index,
								serial_number_confidence
							} = installation;

							if (
								installation.detected_make === device.detected_make &&
								installation.detected_model === device.detected_model
							) {
								relatedInstallations.push({
									// Required
									product_id: matched_catalog_product_id || null,
									property_id: job.property_id,
									installation_date: null,
									warranty_status: InstalledProductWarrantyStatus.Active,
									// Optionals
									product_serial: !serial_number || serial_number === "null" ? "" : serial_number,
									location: location_description,
									// Structured location: AI detection does not resolve the building/floor/room
									// hierarchy, so start null. The user sets it later via the modal, and the DB
									// derives the ancestors from the deepest level on publish.
									building_id: null,
									floor_id: null,
									room_id: null,
									product_notes: additional_notes,
									warranty_expiration_date: null,
									photo_link: pickFirstPhotoLink(visible_in_files),
									attachments: filesToAttachments(installation.visible_in_files),

									// Frontend Local fields
									draft_installed_product_id: nanoid(),
									installation_visible,
									installed_device_index,
									serial_number_confidence
								});
							}
						});

						const hasProductMake =
							detected_make &&
							detected_make.toLowerCase() !== "null" &&
							detected_make.toLowerCase() !== "not specified";

						const productMake = hasProductMake ? detected_make : "[unknown make]";

						const hasProductModel =
							detected_model &&
							detected_model.toLowerCase() !== "null" &&
							detected_model.toLowerCase() !== "not specified";

						const productModel = hasProductModel ? detected_model : "[unknown model]";

						return {
							// Required
							id:
								!matched_catalog_product_id || matched_catalog_product_id === "null"
									? null
									: matched_catalog_product_id,
							name: `${productMake || ""} ${productModel}`.trim(),
							description: visual_description || "",
							type: resolveCategory(detected_category),
							make: hasProductMake ? detected_make : "",
							model: hasProductModel ? detected_model : "",

							// Optionals
							sku: "",
							retail_price: null,
							product_notes: "",
							workspace_id: loggedUserValue.workspace_id,
							organization_id: loggedUserValue.organization_id,
							photo_link: pickFirstPhotoLink(visible_in_files),
							attachments: filesToAttachments(visible_in_files),
							installed_products: relatedInstallations,

							// Frontend Local fields
							draft_product_id: nanoid(),
							match_confidence
						};
					});

					return {
						product_type: productTypeCategory || { name: categoryName, label: categoryName },
						products: productsWithInstalledProducts,
						totalProducts: productsWithInstalledProducts.length,
						totalInstalledProducts: productsWithInstalledProducts.reduce(
							(sum: number, device: any) => sum + device.installed_products.length,
							0
						)
					};
				});

				return { productsGroupedByType: categoriesWithDevices, categoriesList };
			} catch (error) {
				console.error("Error grouping installations:", error);
				return { productsGroupedByType: [], categoriesList: [] };
			}
		},
		[loggedUserValue, getCategoryFromName]
	);

	// ── Subscription ─────────────────────────────────────────────────────────

	const { data: subscriptionData } = useGetDeviceDetectionByIdSubscription({
		skip: !jobId,
		variables: { id: jobId },
		onData: ({ data }) => {
			console.log("Subscription Data: ", data);
			const jobData = data?.data?.device_detection_jobs_by_pk;
			if (!jobData) return;

			const status = jobData.status;
			if (status === DeviceDetectionJobStatus.PENDING || status === DeviceDetectionJobStatus.PROCESSING) return;

			if (
				status === DeviceDetectionJobStatus.DRAFT ||
				status === DeviceDetectionJobStatus.FAILED ||
				status === DeviceDetectionJobStatus.PUBLISHED
			) {
				// Only initialize local state once per job load.
				// After initialization the local state is the source of truth — subsequent
				// subscription fires (triggered by our own DB writes) must NOT overwrite it.
				if (!isInitialized.current) {
					isInitialized.current = true;

					// Resolve grouped products: prefer products_grouped_by_type (already edited),
					// fall back to raw AI output for legacy jobs that were never edited.
					let initialGroups: DeviceDetectionCategory[];
					if (jobData.products_grouped_by_type) {
						initialGroups =
							typeof jobData.products_grouped_by_type === "string"
								? JSON.parse(jobData.products_grouped_by_type)
								: jobData.products_grouped_by_type;
					} else {
						const result = groupProductsByProductType(jobData);
						initialGroups = result.productsGroupedByType as DeviceDetectionCategory[];
					}

					setProductsGroupedByProductType(initialGroups);
					setJob(jobData as Device_Detection_Jobs);

					if (jobData.property) {
						setSelectedProperty(jobData.property as Properties);
					}

					// Notify the consumer so it can navigate to the detail route
					onJobReady(jobData.id);
				} else {
					// After initialization, only update the job metadata (status, etc.)
					// but never overwrite productsGroupedByProductType with the DB round-trip.
					setJob(jobData as Device_Detection_Jobs);
				}
			}
		}
	});

	// ── Attachment handlers ───────────────────────────────────────────────────

	const handleOnCloseAttachmentModal = useCallback(async () => {
		setAddAttachmentModalOpen(false);
		setAddAttachmentModalEditMode(false);
		clearSelectedAttachments();
	}, []);

	const handleOnRemoveAttachment = useCallback((attachmentFile: any) => {
		const { uid } = attachmentFile;
		setAttachmentsFiles(prev => prev.filter(el => el.uid !== uid));
	}, []);

	const updateAttachments = useCallback((attachmentFile: any) => {
		const { uid } = attachmentFile;
		setAttachmentsFiles(prev => {
			const filtered = prev.filter(el => el.uid !== uid);
			return [...filtered, attachmentFile];
		});
	}, []);

	const handleOnClickAttachment = useCallback((file: any) => {
		const { document_link, video_link, photo_link, mime_type, localUrl } = file || {};
		if (document_link) {
			setFileViewer({ type: MediaType.Document, src: document_link });
			return;
		}
		if (video_link) {
			setFileViewer({ type: MediaType.Video, src: video_link });
			return;
		}
		if (photo_link) {
			setFileViewer({ type: MediaType.Image, src: photo_link });
			return;
		}
		if (mime_type && localUrl) {
			setFileViewer({ type: getFileTypeFromMimeType(mime_type), src: localUrl });
		}
	}, []);

	// ── Firebase upload ───────────────────────────────────────────────────────

	const uploadFilesToFirebase = useCallback(async () => {
		if (attachmentsFiles.length === 0) {
			message.warning("Please add at least one file for device detection");
			return null;
		}

		try {
			const promises = attachmentsFiles.map(attachment => {
				const { file, name } = attachment;
				const fileMediaType = file.type.split("/")[0];
				const uniqueID = uuidv4();
				const fileExtension = getFileExtension(file.name);
				const fileName = name ? `${name}.${fileExtension}` : file.name;

				let bucketName = "device-detection-files/";
				let fileTypeName = "";

				switch (fileMediaType) {
					case "image":
						bucketName += "photos";
						fileTypeName = "photo";
						break;
					case "video":
						bucketName += "videos";
						fileTypeName = "video";
						break;
					case "audio":
						bucketName += "audios";
						fileTypeName = "audio";
						break;
					default:
						bucketName += "documents";
						fileTypeName = "document";
						break;
				}

				return (async () => {
					try {
						const attachmentName = `${fileTypeName}-${uniqueID}-${Math.floor(Date.now() / 1000)}-${fileName}`;
						let fileObj = file?.originFileObj || file;
						
						if (/\.(heic|heif)$/i.test(fileObj.name) || fileObj.type === "image/heic" || fileObj.type === "image/heif") {
							try {
								fileObj = await convertHeicFileToJpeg(fileObj);
							} catch (error) {
								console.error("HEIC conversion failed:", error);
							}
						}
						
						const { publicUrl } = await uploadToStorage(fileObj, () => {}, `${bucketName}/${attachmentName}`)
						
						return { file_uri: publicUrl, mime_type: fileObj.type, file_type: fileTypeName };
					} catch (error) {
						console.error("Error uploading file to Firebase:", error);
						return undefined;
					}
				})();
			});

			const uploadedFiles = await Promise.all(promises);
			const filteredFiles = uploadedFiles.filter(Boolean);

			if (filteredFiles.length === 0) {
				message.error("Failed to upload files");
				return null;
			}

			return filteredFiles;
		} catch (error) {
			console.error("Error uploading files to Firebase:", error);
			message.error("Failed to upload files");
			return null;
		}
	}, [attachmentsFiles]);

	// ── Submit (create new job) ───────────────────────────────────────────────

	const handleOnSubmit = useCallback(async () => {
		// Synchronous guard — prevents double-submission from double-click or rapid
		// re-entry before the React state update has had a chance to disable the button.
		if (isSubmittingRef.current) return;
		isSubmittingRef.current = true;

		if (!selectedProperty) {
			message.warning("Please select a property");
			isSubmittingRef.current = false;
			return;
		}
		if (attachmentsFiles.length === 0) {
			message.warning("Please add at least one file for device detection");
			isSubmittingRef.current = false;
			return;
		}

		setUploading(true);

		try {
			const uploadedFiles = await uploadFilesToFirebase();
			if (!uploadedFiles) {
				setUploading(false);
				return;
			}

			await createJobMutation({
				variables: {
					input: {
						property_id: selectedProperty.id,
						workspace_id: selectedProperty.workspace_id,
						organization_id: selectedProperty.organization_id,
						status: DeviceDetectionJobStatus.PENDING,
						input_files: uploadedFiles,
						created_by_user_id: loggedUserValue.id,
						// Link to handover when in handover context
						...(handoverId ? { handover_id: handoverId } : {})
					}
				}
			});
		} catch (error) {
			console.error("Error creating device detection job:", error);
			setUploading(false);
			message.error("Failed to create device detection job");
		} finally {
			isSubmittingRef.current = false;
		}
	}, [selectedProperty, attachmentsFiles, uploadFilesToFirebase, createJobMutation, loggedUserValue, handoverId]);

	// ── Product CRUD ─────────────────────────────────────────────────────────
	//
	// Each action: 1) processes any pending uploads, 2) updates local state
	// optimistically, 3) persists the entire groups array to the DB.
	// The subscription will fire after the DB write but isInitialized guards
	// against it overwriting the local state.

	/**
	 * Persist the current groups snapshot to products_grouped_by_type in the DB.
	 * Call after every local state mutation.
	 */
	const persistGroupsToDb = useCallback(
		async (groups: DeviceDetectionCategory[]) => {
			if (!jobId) return;
			try {
				await updateDeviceDetectionJobByIdMutation({
					variables: { id: jobId, _set: { products_grouped_by_type: groups } }
				});
			} catch (error) {
				console.error("Error persisting groups to DB:", error);
			}
		},
		[jobId, updateDeviceDetectionJobByIdMutation]
	);

	/** Update a product's fields. Handles photo + attachment uploads before saving. */
	const updateProduct = useCallback(
		async (draft_product_id: string, updates: Partial<DetectedDevice>) => {
			const processedUpdates = { ...updates };

			// Step 1 — upload product photo if a local file was attached
			if (processedUpdates.productPhotoFile) {
				try {
					processedUpdates.photo_link = await uploadProductPhoto(
						processedUpdates.productPhotoFile,
						processedUpdates.name || "product"
					);
					delete processedUpdates.productPhotoFile;
				} catch (error) {
					console.error("Error uploading product photo:", error);
				}
			}

			// Step 2 — upload attachments if any local files are present
			if (processedUpdates.attachments) {
				try {
					const uploaded = await processProductAttachments(processedUpdates.attachments, "products-attachments");
					processedUpdates.attachments = uploaded;
					if (!processedUpdates.photo_link) {
						processedUpdates.photo_link = pickFirstPhotoLink(uploaded) ?? undefined;
					}
				} catch (error) {
					console.error("Error uploading product attachments:", error);
				}
			}

			// Step 3 — compute new groups and update local state + DB atomically
			let newGroups: DeviceDetectionCategory[] = [];

			setProductsGroupedByProductType(prev => {
				const groups = [...prev];
				const found = findProductByDraftId(groups, draft_product_id);
				if (!found) {
					console.error(`Product with draft_product_id ${draft_product_id} not found`);
					newGroups = prev;
					return prev;
				}

				const { groupIdx, productIdx } = found;
				const updatedProduct: DetectedDevice = {
					...groups[groupIdx].products[productIdx],
					...processedUpdates
				};

				const oldTypeName = groups[groupIdx].product_type?.name;
				const newTypeName = updatedProduct.type?.name;
				const typeChanged = newTypeName && newTypeName !== oldTypeName;

				if (typeChanged) {
					// Remove from source group
					groups[groupIdx] = recalculateGroupTotals({
						...groups[groupIdx],
						products: groups[groupIdx].products.filter(p => p.draft_product_id !== draft_product_id)
					});
					// Add to destination group (create it if it doesn't exist yet)
					const destIdx = groups.findIndex(g => g.product_type?.name === newTypeName);
					if (destIdx !== -1) {
						groups[destIdx] = recalculateGroupTotals({
							...groups[destIdx],
							products: [...groups[destIdx].products, updatedProduct]
						});
					} else {
						groups.push(
							recalculateGroupTotals({
								product_type: updatedProduct.type,
								products: [updatedProduct],
								totalProducts: 0,
								totalInstalledProducts: 0
							})
						);
					}
					newGroups = removeEmptyGroups(groups);
				} else {
					// In-place update — type unchanged
					const updatedProducts = [...groups[groupIdx].products];
					updatedProducts[productIdx] = updatedProduct;
					groups[groupIdx] = { ...groups[groupIdx], products: updatedProducts };
					newGroups = groups;
				}

				return newGroups;
			});

			await persistGroupsToDb(newGroups);
		},
		[persistGroupsToDb]
	);

	/** Delete a product and all its installed products. */
	const deleteProduct = useCallback(
		async (draft_product_id: string) => {
			let newGroups: DeviceDetectionCategory[] = [];

			setProductsGroupedByProductType(prev => {
				const groups = [...prev];
				const found = findProductByDraftId(groups, draft_product_id);
				if (!found) {
					console.error(`Product with draft_product_id ${draft_product_id} not found`);
					newGroups = prev;
					return prev;
				}
				const { groupIdx } = found;
				groups[groupIdx] = recalculateGroupTotals({
					...groups[groupIdx],
					products: groups[groupIdx].products.filter(p => p.draft_product_id !== draft_product_id)
				});
				newGroups = removeEmptyGroups(groups);
				return newGroups;
			});

			await persistGroupsToDb(newGroups);
		},
		[persistGroupsToDb]
	);

	const handleConfirmProductDeletion = useCallback((product: any) => {
		if (product.draft_product_id) {
			deleteProduct(product.draft_product_id);
		}
	}, []);

	/** Update an installed product's fields. Handles photo + attachment uploads before saving. */
	const updateInstalledProduct = useCallback(
		async (draft_installed_product_id: string, updates: Partial<DeviceInstallation>) => {
			const processedUpdates = { ...updates };

			// Step 1 — upload installed product photo if a local file was attached
			if (processedUpdates.installedProductPhotoFile) {
				try {
					processedUpdates.photo_link = await uploadInstalledProductPhoto(
						processedUpdates.installedProductPhotoFile,
						processedUpdates.product_serial || "installed-product"
					);
					delete processedUpdates.installedProductPhotoFile;
				} catch (error) {
					console.error("Error uploading installed product photo:", error);
				}
			}

			// Step 2 — upload attachments if any local files are present
			if (processedUpdates.attachments) {
				try {
					const uploaded = await processProductAttachments(
						processedUpdates.attachments,
						"installed-product-attachments"
					);
					processedUpdates.attachments = uploaded;
					if (!processedUpdates.photo_link) {
						processedUpdates.photo_link = pickFirstPhotoLink(uploaded) ?? undefined;
					}
				} catch (error) {
					console.error("Error uploading installed product attachments:", error);
				}
			}

			// Step 3 — compute new groups and update local state + DB atomically
			let newGroups: DeviceDetectionCategory[] = [];

			setProductsGroupedByProductType(prev => {
				const groups = [...prev];
				const found = findInstalledProductByDraftId(groups, draft_installed_product_id);
				if (!found) {
					console.error(`Installed product ${draft_installed_product_id} not found`);
					newGroups = prev;
					return prev;
				}
				const { groupIdx, productIdx, installedIdx } = found;
				const updatedInstalled = [...groups[groupIdx].products[productIdx].installed_products];
				updatedInstalled[installedIdx] = {
					...updatedInstalled[installedIdx],
					...processedUpdates
				};
				const updatedProducts = [...groups[groupIdx].products];
				updatedProducts[productIdx] = {
					...updatedProducts[productIdx],
					installed_products: updatedInstalled
				};
				groups[groupIdx] = { ...groups[groupIdx], products: updatedProducts };
				newGroups = groups;
				return newGroups;
			});

			await persistGroupsToDb(newGroups);
		},
		[persistGroupsToDb]
	);

	/** Delete an installed product. */
	const deleteInstalledProduct = useCallback(
		async (draft_installed_product_id: string) => {
			let newGroups: DeviceDetectionCategory[] = [];

			setProductsGroupedByProductType(prev => {
				const groups = [...prev];
				const found = findInstalledProductByDraftId(groups, draft_installed_product_id);
				if (!found) {
					console.error(`Installed product ${draft_installed_product_id} not found`);
					newGroups = prev;
					return prev;
				}
				const { groupIdx, productIdx } = found;
				const updatedProducts = [...groups[groupIdx].products];
				updatedProducts[productIdx] = {
					...updatedProducts[productIdx],
					installed_products: updatedProducts[productIdx].installed_products.filter(
						ip => ip.draft_installed_product_id !== draft_installed_product_id
					)
				};
				groups[groupIdx] = recalculateGroupTotals({ ...groups[groupIdx], products: updatedProducts });
				newGroups = groups;
				return newGroups;
			});

			await persistGroupsToDb(newGroups);
		},
		[persistGroupsToDb]
	);

	const handleConfirmInstalledProductDeletion = useCallback((ip: any) => {
		if (ip.draft_installed_product_id) {
			deleteInstalledProduct(ip.draft_installed_product_id);
		}
	}, []);

	/** Add a new installed product to a specific product (by draft_product_id). */
	const addInstalledProduct = useCallback(
		async (draft_product_id: string, partialInstall: Partial<DeviceInstallation>) => {
			let newGroups: DeviceDetectionCategory[] = [];

			// 1 — upload photo if a local file was provided
			const processedInstall = { ...partialInstall };
			if (processedInstall.installedProductPhotoFile) {
				try {
					processedInstall.photo_link = await uploadInstalledProductPhoto(
						processedInstall.installedProductPhotoFile,
						processedInstall.product_serial || "installed-product"
					);
					delete processedInstall.installedProductPhotoFile;
				} catch (error) {
					console.error("Error uploading installed product photo:", error);
				}
			}

			// 2 — upload attachments if any local files are present
			if (processedInstall.attachments && processedInstall.attachments.length > 0) {
				try {
					const uploaded = await processProductAttachments(
						processedInstall.attachments,
						"installed-product-attachments"
					);
					processedInstall.attachments = uploaded;
					if (!processedInstall.photo_link) {
						processedInstall.photo_link = pickFirstPhotoLink(uploaded) ?? undefined;
					}
				} catch (error) {
					console.error("Error uploading installed product attachments:", error);
				}
			}

			// 3 — build the new installation object
			const found = findProductByDraftId(productsGroupedByProductType, draft_product_id);
			const parentProduct = found ? productsGroupedByProductType[found.groupIdx].products[found.productIdx] : null;

			const newInstall: DeviceInstallation = {
				property_id: selectedProperty?.id || "",
				product_id: parentProduct?.id || null,
				installation_date: null,
				warranty_status: InstalledProductWarrantyStatus.Active,
				product_serial: "",
				location: "",
				// Structured location defaults; `...processedInstall` overrides with the modal selection.
				building_id: null,
				floor_id: null,
				room_id: null,
				product_notes: "",
				warranty_expiration_date: null,
				photo_link: null,
				draft_installed_product_id: nanoid(),
				...processedInstall
			};

			// 4 — update local state + persist
			setProductsGroupedByProductType(prev => {
				const groups = [...prev];
				const f = findProductByDraftId(groups, draft_product_id);
				if (!f) {
					newGroups = prev;
					return prev;
				}
				const { groupIdx, productIdx } = f;
				const updatedProducts = [...groups[groupIdx].products];
				updatedProducts[productIdx] = {
					...updatedProducts[productIdx],
					installed_products: [...updatedProducts[productIdx].installed_products, newInstall]
				};
				groups[groupIdx] = recalculateGroupTotals({ ...groups[groupIdx], products: updatedProducts });
				newGroups = groups;
				return newGroups;
			});

			await persistGroupsToDb(newGroups);
		},
		[selectedProperty, productsGroupedByProductType, persistGroupsToDb]
	);

	/** Add a brand-new product (manually, not from AI detection) to the groups. */
	const addProduct = useCallback(
		async (partialProduct: Partial<DetectedDevice>) => {
			const processedProduct = { ...partialProduct };

			if (processedProduct.productPhotoFile) {
				try {
					processedProduct.photo_link = await uploadProductPhoto(
						processedProduct.productPhotoFile,
						processedProduct.name || "product"
					);
					delete processedProduct.productPhotoFile;
				} catch (error) {
					console.error("Error uploading product photo:", error);
				}
			}

			if (processedProduct.attachments) {
				try {
					const uploaded = await processProductAttachments(processedProduct.attachments, "products-attachments");
					processedProduct.attachments = uploaded;
					if (!processedProduct.photo_link) {
						processedProduct.photo_link = pickFirstPhotoLink(uploaded) ?? undefined;
					}
				} catch (error) {
					console.error("Error uploading product attachments:", error);
				}
			}

			const newProduct: DetectedDevice = {
				draft_product_id: nanoid(),
				name: "",
				description: "",
				make: "",
				model: "",
				type: { name: "UNCATEGORIZED", label: "Uncategorized" },
				installed_products: [],
				...processedProduct
			};

			let newGroups: DeviceDetectionCategory[] = [];

			setProductsGroupedByProductType(prev => {
				const groups = [...prev];
				const typeName = newProduct.type?.name;
				const destIdx = groups.findIndex(g => g.product_type?.name === typeName);

				if (destIdx !== -1) {
					groups[destIdx] = recalculateGroupTotals({
						...groups[destIdx],
						products: [...groups[destIdx].products, newProduct]
					});
				} else {
					groups.push(
						recalculateGroupTotals({
							product_type: newProduct.type,
							products: [newProduct],
							totalProducts: 0,
							totalInstalledProducts: 0
						})
					);
				}

				newGroups = groups;
				return newGroups;
			});

			await persistGroupsToDb(newGroups);
		},
		[persistGroupsToDb]
	);

	// ── Publish detection (accept AI results, persist to DB) ─────────────────

	const publishDetection = useCallback(async () => {
		try {
			setIsPublishing(true);

			const allProducts = (productsGroupedByProductType ?? []).flatMap((g: any) => g.products ?? []).filter(Boolean);

			const formattedProductsPromises = allProducts.map(async (p: any) => {
				const { draft_product_id, match_confidence, productPhotoFile, ...prodInput } = p;

				if (productPhotoFile) {
					try {
						prodInput.photo_link = await uploadProductPhoto(productPhotoFile, p.name || "product");
					} catch (error) {
						console.error("Error uploading product photo:", error);
					}
				}

				if (p.attachments && p.attachments.length > 0) {
					prodInput.attachments = await processProductAttachments(p.attachments, "products-attachments");
					if (!prodInput.photo_link) {
						prodInput.photo_link = pickFirstPhotoLink(prodInput.attachments);
					}
				} else {
					prodInput.attachments = undefined;
					prodInput.photo_link = prodInput.photo_link ?? null;
				}

				if (p.installed_products && p.installed_products.length > 0) {
					prodInput.installed_products = await Promise.all(
						p.installed_products.filter(Boolean).map(async (ip: any) => {
							const { installedProductPhotoFile, ...ipData } = ip;

							if (installedProductPhotoFile) {
								try {
									ipData.photo_link = await uploadInstalledProductPhoto(
										installedProductPhotoFile,
										ip.product_serial || "installed-product"
									);
								} catch (error) {
									console.error("Error uploading installed product photo:", error);
								}
							}

							if (ip.attachments && ip.attachments.length > 0) {
								const uploadedAttachments = await processProductAttachments(
									ip.attachments,
									"installed-product-attachments"
								);
								if (!ipData.photo_link) {
									ipData.photo_link = pickFirstPhotoLink(uploadedAttachments);
								}
								ipData.attachments = uploadedAttachments;
							}

							const formattedIp = formatInstalledProduct(ipData);

							// Add handover_id and device_detection_job_id to installed product
							if (handoverId) {
								formattedIp.handover_id = handoverId;
							}
							if (jobId) {
								formattedIp.device_detection_job_id = jobId;
							}

							return formattedIp;
						})
					);
				}

				// Convert ProductCategory object to string for the DB
				if (prodInput.type && typeof prodInput.type === "object") {
					// @ts-ignore
					prodInput.type = prodInput.type.name;
				}

				// Add handover_id and device_detection_job_id to product
				if (handoverId) {
					prodInput.handover_id = handoverId;
				}
				if (jobId) {
					prodInput.device_detection_job_id = jobId;
				}

				return prodInput;
			});

			// @ts-ignore
			const formattedProducts: Array<ProductWithInstalledProductsInput> = await Promise.all(formattedProductsPromises);
			const input = pruneEmptyValues({ products: formattedProducts });

			// Persist grouped products before publish to prevent null and UI issues.
			await persistGroupsToDb(productsGroupedByProductType);

			// Run product creation + optional handover notes update in parallel
			const mutations: Promise<any>[] = [createProductsWithInstallationsMutation({ variables: { input } })];

			if (handoverId) {
				mutations.push(
					updateHandoverByIdMutation({
						variables: {
							id: handoverId,
							set: { vendor_device_notes: vendorDeviceNotes || null }
						}
					})
				);
			}

			await Promise.all(mutations);
		} catch (error) {
			console.error("Error in publishDetection:", error);
			message.error("Failed to upload files. Please try again.");
		} finally {
			setIsPublishing(false);
		}
	}, [
		productsGroupedByProductType,
		createProductsWithInstallationsMutation,
		handoverId,
		updateHandoverByIdMutation,
		vendorDeviceNotes,
		persistGroupsToDb,
		jobId
	]);

	// ── Derived ──────────────────────────────────────────────────────────────

	const isLoading =
		uploading ||
		propertiesLoading ||
		loadingCreateProducts ||
		loadingUpdateJob ||
		isPublishing ||
		loadingUpdateHandover;

	// Note: both arms refer to the same job — subscriptionData updates metadata (status),
	// local `job` state is the fallback before the subscription fires.
	const processingData = subscriptionData?.device_detection_jobs_by_pk || job;

	return {
		// State
		propertyOptions,
		selectedProperty,
		setSelectedProperty,
		attachmentsFiles,
		addAttachmentModalOpen,
		setAddAttachmentModalOpen,
		addAttachmentModalEditMode,
		jobId,
		setJobId,
		processingStarted,
		setProcessingStarted,
		setAttachmentsFiles,
		vendorDeviceNotes,
		setVendorDeviceNotes,
		productModalOpen,
		selectedProduct,
		installedProductModalOpen,
		selectedInstallation,
		addInstallModalOpen,
		selectedProductForNewInstall,
		addProductModalOpen,
		fileViewer,
		setFileViewer,
		isLoading,
		propertiesLoading,
		uploadingDetectionFiles: uploading,
		allInstalledProductsSkipped,

		// Job data
		job,
		productsGroupedByProductType,

		// Subscription
		subscriptionData,
		processingData,

		// Handlers
		handleOnBack: onBack,
		handleOpenProductModal,
		handleCloseProductModal,
		handleConfirmProductDeletion,
		handleOpenInstalledProductModal,
		handleCloseInstalledProductModal,
		handleConfirmInstalledProductDeletion,
		handleOpenAddInstallModal,
		handleCloseAddInstallModal,
		addInstalledProduct,
		handleOpenAddProductModal,
		handleCloseAddProductModal,
		addProduct,
		handleOnCloseAttachmentModal,
		handleOnRemoveAttachment,
		updateAttachments,
		handleOnClickAttachment,
		handleOnSubmit,
		publishDetection,
		updateProduct,
		updateInstalledProduct,

		// Helpers
		getLabelFromName
	};
};
